import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import {
  REFERRAL_BONUS_MINOR, REFERRAL_QUALIFY_MINOR, formatMinor,
  type ArtistReferral, type ArtistReferralStatus,
} from '@eprintsmart/shared';
import { payArtistReferral, watchArtistReferrals } from '../services/referrals';

/**
 * Artist-to-artist referrals, and the ₹1,000-a-side that hangs on them.
 *
 * PAYING IS A HUMAN ACT ON PURPOSE. The nightly evaluator carries a referral as
 * far as PAYABLE and stops. ₹2,000 leaving on a schedule with nobody looking is
 * how a bug becomes a bank transfer, and at single digits a month the click
 * costs nothing.
 *
 * The screen therefore has to show enough to decide on, not just a total. That
 * means the progress toward ₹8,000 and — when something was left out — WHY. A
 * figure with three orders quietly missing is exactly what a dispute is made
 * of, and the artist will have their own number from their own studio page.
 */

const COLOUR: Record<ArtistReferralStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  PENDING: 'default',
  HELD: 'info',
  PAYABLE: 'warning',
  PAID: 'success',
  VOID: 'error',
};

const WHAT_IT_MEANS: Record<ArtistReferralStatus, string> = {
  PENDING: 'not there yet',
  HELD: 'qualified — 30-day return window running',
  PAYABLE: 'ready to pay',
  PAID: 'done',
  VOID: 'closed',
};

const when = (ms?: number) =>
  ms ? new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function Referrals() {
  const [rows, setRows] = useState<ArtistReferral[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => watchArtistReferrals(setRows), []);

  const payable = useMemo(() => rows.filter((r) => r.status === 'PAYABLE'), [rows]);
  const owed = payable.length * REFERRAL_BONUS_MINOR * 2;

  const pay = async (referral: ArtistReferral) => {
    setBusy(referral.id);
    setError(null);
    setNote(null);
    try {
      await payArtistReferral(referral.id);
      setNote(
        `Paid. ${formatMinor(REFERRAL_BONUS_MINOR)} to each side — this records the `
        + 'decision; the money goes out with their next settlement.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That could not be paid.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Artist referrals</Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMinor(REFERRAL_BONUS_MINOR)} to each side once a referred artist&rsquo;s
          work has sold {formatMinor(REFERRAL_QUALIFY_MINOR)} and their profile is
          complete. Commissions and self-purchases never count; refunds come off.
        </Typography>
      </Box>

      {payable.length > 0 && (
        <Alert severity="warning">
          <strong>{payable.length}</strong> ready to pay — {formatMinor(owed)} in total
          across both sides.
        </Alert>
      )}
      {note && <Alert severity="success" onClose={() => setNote(null)}>{note}</Alert>}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Referred artist</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Qualified</TableCell>
              <TableCell>Payable from</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No referrals yet. One is created when an approved artist named
                    somebody in &ldquo;who told you about us&rdquo;.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography variant="body2">
                    {r.referredDisplayName ?? r.referredDesignerId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.referredHandle ? `@${r.referredHandle} · ` : ''}
                    referred by {r.code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatMinor(r.qualifyingMinor)} / {formatMinor(REFERRAL_QUALIFY_MINOR)}
                  </Typography>
                  {/* The reason a referral is not moving is the thing worth
                      reading. Without it this column is a number nobody can act on. */}
                  {r.voidReason && (
                    <Typography variant="caption" color="text.secondary">
                      {r.voidReason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={COLOUR[r.status]} label={r.status} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {WHAT_IT_MEANS[r.status]}
                  </Typography>
                </TableCell>
                <TableCell>{when(r.qualifiedAt)}</TableCell>
                <TableCell>{when(r.payableAt)}</TableCell>
                <TableCell align="right">
                  {r.status === 'PAYABLE' ? (
                    <Button size="small" variant="contained"
                      disabled={busy !== null}
                      onClick={() => void pay(r)}>
                      Pay {formatMinor(REFERRAL_BONUS_MINOR)} each
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {r.status === 'PAID' ? when(r.paidAt) : '—'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        Nothing pays itself. The nightly job moves a referral to PAYABLE and stops
        there; this screen is the only thing that marks one paid, and it is
        idempotent — pressing it twice pays once.
      </Typography>
    </Stack>
  );
}
